import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  CircularProgress,
  Alert,
  IconButton,
  Toolbar,
  Chip
} from '@mui/material';
import {
  Close as CloseIcon,
  Download as DownloadIcon,
  Security as SecurityIcon,
  Fullscreen as FullscreenIcon,
  FullscreenExit as FullscreenExitIcon,
  OpenInNew as OpenInNewIcon
} from '@mui/icons-material';
import { useSecureDocuments } from '../hooks/useSecureDocuments';
import { GlobalWorkerOptions, getDocument } from 'pdfjs-dist';

// Point PDF.js to its worker (bundled from node_modules)
GlobalWorkerOptions.workerSrc = new URL(
  'pdfjs-dist/build/pdf.worker.min.js',
  import.meta.url
).toString();

const DocumentViewerModal = ({ 
  open, 
  onClose, 
  documentId, 
  documentName = 'Document',
  documentInfo = null 
}) => {
  const [blobUrl, setBlobUrl] = useState(null);
  const [contentType, setContentType] = useState('');
  const [pdfPageImage, setPdfPageImage] = useState(null);
  const [pdfRenderError, setPdfRenderError] = useState(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [error, setError] = useState(null);
  const [loadingDocument, setLoadingDocument] = useState(false);

  const [inlineUrl, setInlineUrl] = useState(null);
  
  const { 
    getSecureAccess, 
    downloadSecureDocument,
    loading: hookLoading 
  } = useSecureDocuments();

  // Clean up blob URL when modal closes
  useEffect(() => {
    return () => {
      if (blobUrl) {
        URL.revokeObjectURL(blobUrl);
      }
    };
  }, [blobUrl]);

  // Load document when modal opens
  useEffect(() => {
    if (open && documentId && !blobUrl) {
      loadDocument();
    }
  }, [open, documentId, blobUrl]);

  const loadDocument = async () => {
    setLoadingDocument(true);
    setError(null);
    
    try {
      // PERFORMANCE OPTIMIZATION: Use optimized endpoint for faster loading
      // Get secure access token (same security)
      const accessData = await getSecureAccess(documentId);
      
      // Use optimized endpoint that returns HTML with embedded iframe
      const optimizedUrl = `/api/documents/${documentId}/view-optimized?token=${accessData.secure_url.split('?token=')[1]}`;
      setInlineUrl(optimizedUrl);
      
      // For compatibility, still create a blob URL from the secure endpoint
      // But only if we need it (for downloads or fallback display)
      const token = localStorage.getItem('auth_token');
      const headers = {
        'Authorization': `Bearer ${token}`,
      };

      // For images and other content types, we might still need the blob
      const documentUrl = accessData.secure_url.startsWith('http') 
        ? accessData.secure_url 
        : `${window.location.origin}${accessData.secure_url}`;

      const response = await fetch(documentUrl, {
        method: 'GET',
        headers,
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch document: ${response.statusText}`);
      }

      const blob = await response.blob();

      // Detect PDF by signature if content-type is missing or generic
      let detectedType = blob.type || '';
      if (!detectedType || detectedType === 'application/octet-stream') {
        try {
          const header = await blob.slice(0, 4).arrayBuffer();
          const bytes = new Uint8Array(header);
          const isPdfSignature = bytes[0] === 0x25 && bytes[1] === 0x50 && bytes[2] === 0x44 && bytes[3] === 0x46; // %PDF
          if (isPdfSignature) {
            detectedType = 'application/pdf';
          }
        } catch (sigErr) {
          console.warn('Could not inspect blob signature', sigErr);
        }
      }

      const blobObjectUrl = URL.createObjectURL(blob);
      
      setBlobUrl(blobObjectUrl);
      setContentType(detectedType || 'application/octet-stream');

      // Kick off PDF render if applicable
      if (isPdf(detectedType, documentName) || isPdf(contentType, documentName)) {
        renderPdfToImage(blobObjectUrl);
      }
      
    } catch (err) {
      console.error('Failed to load document:', err);
      setError(`Failed to load document: ${err.message}`);
    } finally {
      setLoadingDocument(false);
    }
  };

  const handleDownload = async () => {
    try {
      await downloadSecureDocument(documentId, documentName);
    } catch (err) {
      setError(`Failed to download document: ${err.message}`);
    }
  };

  const handleOpenInNewTab = () => {
    if (blobUrl) {
      const newWindow = window.open(blobUrl, '_blank', 'noopener,noreferrer');
      if (!newWindow) {
        setError('Popup blocked. Please allow popups for this site and try again.');
      }
    }
  };



  const handleClose = () => {
    // Blur any focused element to prevent aria-hidden focus warnings
    if (document.activeElement && document.activeElement.blur) {
      document.activeElement.blur();
    }
    
    // Clean up blob URL
    if (blobUrl) {
      URL.revokeObjectURL(blobUrl);
      setBlobUrl(null);
    }
    setError(null);
    setIsFullscreen(false);
    setPdfPageImage(null);
    setPdfRenderError(null);
    onClose();
  };

  const toggleFullscreen = () => {
    setIsFullscreen(!isFullscreen);
  };

  const getFileExtension = (fileName) => {
    if (!fileName) return '';
    return fileName.split('.').pop()?.toLowerCase() || '';
  };

  const isImage = (type, fileName) => {
    const imageTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp', 'image/svg+xml'];
    const imageExtensions = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'];
    const extension = getFileExtension(fileName);
    
    return imageTypes.includes(type) || imageExtensions.includes(extension);
  };

  const isPdf = (type, fileName) => {
    const extension = getFileExtension(fileName);
    const normalizedType = (type || '').toLowerCase();
    return normalizedType.includes('pdf') || extension === 'pdf';
  };

  const renderPdfToImage = async (url) => {
    try {
      setPdfRenderError(null);
      const loadingTask = getDocument(url);
      const pdf = await loadingTask.promise;
      const page = await pdf.getPage(1);
      const viewport = page.getViewport({ scale: 1.5 });
      const canvas = document.createElement('canvas');
      const context = canvas.getContext('2d');
      canvas.width = viewport.width;
      canvas.height = viewport.height;
      await page.render({ canvasContext: context, viewport }).promise;
      const dataUrl = canvas.toDataURL('image/png');
      setPdfPageImage(dataUrl);
    } catch (err) {
      console.error('PDF render failed', err);
      setPdfRenderError('PDF preview could not be rendered.');
    }
  };

  const renderDocumentContent = () => {
    if (loadingDocument || hookLoading) {
      return (
        <Box 
          sx={{ 
            display: 'flex', 
            flexDirection: 'column',
            alignItems: 'center', 
            justifyContent: 'center', 
            minHeight: '400px',
            gap: 2
          }}
        >
          <CircularProgress size={48} />
          <Typography variant="body1" color="text.secondary">
            Loading secure document...
          </Typography>
        </Box>
      );
    }

    if (error) {
      return (
        <Box sx={{ p: 3 }}>
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
          <Button variant="outlined" onClick={loadDocument}>
            Retry
          </Button>
        </Box>
      );
    }

    if (!blobUrl) {
      return (
        <Box sx={{ p: 3 }}>
          <Alert severity="info">
            No document content available
          </Alert>
        </Box>
      );
    }

    const fileName = documentName || documentInfo?.file_name || '';

    // Render based on content type
    if (isImage(contentType, fileName)) {
      return (
        <Box 
          sx={{ 
            display: 'flex', 
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '400px',
            p: 2,
            backgroundColor: 'grey.50'
          }}
        >
          <img 
            src={blobUrl} 
            alt={fileName}
            style={{
              maxWidth: '100%',
              maxHeight: isFullscreen ? '80vh' : '60vh',
              objectFit: 'contain',
              border: '1px solid #ddd',
              borderRadius: '4px'
            }}
          />
        </Box>
      );
    }

    if (isPdf(contentType, fileName)) {
      // Prefer same-origin blob rendering via PDF.js; show loading/placeholder until ready
      if (blobUrl) {
        if (pdfPageImage) {
          return (
            <Box sx={{ width: '100%', height: isFullscreen ? '80vh' : '60vh', display: 'flex', justifyContent: 'center', alignItems: 'center', backgroundColor: 'grey.50' }}>
              <img
                src={pdfPageImage}
                alt={`${fileName} preview`}
                style={{
                  maxWidth: '100%',
                  maxHeight: isFullscreen ? '90vh' : '70vh',
                  objectFit: 'contain'
                }}
              />
            </Box>
          );
        }

        // While PDF.js is rendering, show a simple placeholder
        return (
          <Box sx={{ width: '100%', height: isFullscreen ? '80vh' : '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              Preparing PDF preview...
            </Typography>
          </Box>
        );
      }

      // If the blob isn't ready yet, show a friendly message instead of a blank iframe
      return (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '300px',
            gap: 2,
            p: 2,
            textAlign: 'center'
          }}
        >
          <Typography variant="body1" color="text.secondary">
            Preparing document preview...
          </Typography>
          <Typography variant="body2" color="text.secondary">
            If the preview does not load, use “Open in New Tab” or “Download”.
          </Typography>
        </Box>
      );
    }

    // For other file types, show download option
    return (
      <Box 
        sx={{ 
          display: 'flex', 
          flexDirection: 'column',
          alignItems: 'center', 
          justifyContent: 'center', 
          minHeight: '400px',
          gap: 2,
          p: 3
        }}
      >
        <Typography variant="h6" color="text.secondary">
          Preview not available for this file type
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Content Type: {contentType}
        </Typography>
        <Button 
          variant="contained" 
          startIcon={<DownloadIcon />}
          onClick={handleDownload}
        >
          Download to View
        </Button>
      </Box>
    );
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth={isFullscreen ? false : "lg"}
      fullWidth
      fullScreen={isFullscreen}
      PaperProps={{
        sx: {
          minHeight: isFullscreen ? '100vh' : '70vh',
          maxHeight: isFullscreen ? '100vh' : '90vh'
        }
      }}
    >
      <DialogTitle sx={{ p: 0 }}>
        <Toolbar sx={{ pl: 2, pr: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', flex: 1, gap: 2 }}>
            <SecurityIcon color="primary" />
            <Box>
              <Typography variant="h6" component="div">
                {documentName || 'Document Viewer'}
              </Typography>
              {documentInfo?.gcs_path && (
                <Chip 
                  icon={<SecurityIcon />} 
                  label="Secure Access" 
                  size="small" 
                  color="success" 
                  sx={{ mt: 0.5 }}
                />
              )}
            </Box>
          </Box>
          
          <Box sx={{ display: 'flex', gap: 1 }}>
            <IconButton 
              onClick={handleDownload}
              disabled={!blobUrl || loadingDocument}
              title="Download Document"
            >
              <DownloadIcon />
            </IconButton>
            
            {blobUrl && (
              <IconButton 
                onClick={handleOpenInNewTab}
                title="Open in New Tab"
              >
                <OpenInNewIcon />
              </IconButton>
            )}
            
            <IconButton 
              onClick={toggleFullscreen}
              title={isFullscreen ? "Exit Fullscreen" : "Fullscreen"}
            >
              {isFullscreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
            </IconButton>
            
            <IconButton 
              onClick={handleClose}
              title="Close"
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </Toolbar>
      </DialogTitle>
      
      <DialogContent sx={{ p: 0, overflow: 'hidden' }}>
        {renderDocumentContent()}
      </DialogContent>
      
      {!isFullscreen && (
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }}>
            🔒 Secure access • Token expires in 5 minutes
          </Typography>
          <Button onClick={handleClose}>
            Close
          </Button>
        </DialogActions>
      )}
    </Dialog>
  );
};

export default DocumentViewerModal; 
